<template>
  <div class="lp-root">
    <!-- Ambient orbs — parallax on scroll -->
    <div class="lp-orb lp-orb-1" :style="orb1Style" aria-hidden="true"><div class="lp-orb-inner"></div></div>
    <div class="lp-orb lp-orb-2" :style="orb2Style" aria-hidden="true"><div class="lp-orb-inner"></div></div>
    <div class="lp-orb lp-orb-3" :style="orb3Style" aria-hidden="true"><div class="lp-orb-inner"></div></div>

    <!-- ── Navigation ──────────────────────────────────────────────── -->
    <header class="lp-nav" role="banner">
      <div class="lp-nav-inner">
        <div class="lp-logo">
          <img :src="brandLogo" alt="BeyondWebsite.ai logo" />
        </div>
        <nav class="lp-nav-center" aria-label="Main navigation">
          <RouterLink to="/" class="lp-nav-pill">Home</RouterLink>
          <RouterLink to="/pricing" class="lp-nav-pill">Pricing</RouterLink>
        </nav>
        <div class="lp-nav-actions">
          <RouterLink to="/login" class="lp-login-btn">Sign in</RouterLink>
        </div>
      </div>
    </header>

    <!-- ── Hero ───────────────────────────────────────────────────── -->
    <section class="lp-hero" aria-labelledby="hero-heading">
      <p class="lp-eyebrow">AI Website Builder</p>
      <h1 id="hero-heading">
        Build and launch your website<br />
        <span class="lp-gradient-text">instantly with AI.</span><br />
        No code required.
      </h1>
      <p class="lp-hero-sub">
        Just show us a website you like, add your brand, and we'll handle everything else.
        Your perfect website, ready in minutes — SEO, AEO, and GEO built in from day one.
      </p>
      <div class="lp-hero-actions">
        <RouterLink to="/login" class="lp-btn-primary">
          Get started free
          <svg width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </RouterLink>
        <span class="lp-hero-note">No credit card needed</span>
      </div>

      <!-- Floating stats -->
      <div class="lp-hero-stats" role="list">
        <div role="listitem">
          <strong>SEO-ready</strong>
          <span>Ranks from day one</span>
        </div>
        <div role="listitem">
          <strong>AEO-aware</strong>
          <span>Surfaces in AI answers</span>
        </div>
        <div role="listitem">
          <strong>GEO-minded</strong>
          <span>Generative search optimized</span>
        </div>
        <div role="listitem">
          <strong>Minutes</strong>
          <span>From brief to live site</span>
        </div>
      </div>
    </section>

    <!-- ── How it works ───────────────────────────────────────────── -->
    <section class="lp-section lp-how" aria-labelledby="how-heading">
      <div class="lp-section-inner">
        <p class="lp-eyebrow">How it works</p>
        <h2 id="how-heading">Three steps to your perfect site</h2>
        <p class="lp-section-sub">No templates. No limitations. Just results.</p>

        <ol class="lp-steps" aria-label="Three-step process">
          <li class="lp-step-card glass">
            <span class="lp-step-num" aria-hidden="true">01</span>
            <div class="lp-step-icon" aria-hidden="true">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M10.172 13.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.1-1.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </div>
            <h3>Share Inspiration</h3>
            <p>Paste the URL of any website you love. Our AI analyses the structure, layout, and design language in seconds.</p>
          </li>
          <li class="lp-step-card glass">
            <span class="lp-step-num" aria-hidden="true">02</span>
            <div class="lp-step-icon" aria-hidden="true">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/><path d="M12 8v4l3 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </div>
            <h3>Add Your Brand</h3>
            <p>Upload your logo and colours. The AI adapts the entire design system to match your identity automatically.</p>
          </li>
          <li class="lp-step-card glass">
            <span class="lp-step-num" aria-hidden="true">03</span>
            <div class="lp-step-icon" aria-hidden="true">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3>Get Your Site</h3>
            <p>Receive a fully customised, launch-ready website with clean code, full codebase access, and built-in SEO within minutes.</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ── Features ───────────────────────────────────────────────── -->
    <section class="lp-section lp-features" aria-labelledby="features-heading">
      <div class="lp-section-inner">
        <p class="lp-eyebrow">Why BeyondWebsite.ai</p>
        <h2 id="features-heading">Everything you need to launch fast</h2>
        <p class="lp-section-sub">Professional websites without the agency price tag or the template compromise.</p>

        <ul class="lp-features-grid" aria-label="Feature list">
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3>Full Codebase Access</h3>
            <p>Your site is real code — Nuxt 3, clean and developer-ready. Scale, extend, and deploy it anywhere.</p>
          </li>
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3>Perfect Design Replication</h3>
            <p>AI reverse-engineers structure, spacing, and visual hierarchy — not just colours and fonts.</p>
          </li>
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3>Ready in Minutes</h3>
            <p>What used to take weeks of design and development now takes minutes. No waiting. No sprints.</p>
          </li>
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.8"/><path d="M21 21l-4.35-4.35" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </div>
            <h3>SEO, AEO & GEO Built-in</h3>
            <p>Structured data, meta tags, and AI-answer optimisation are baked into every page from the start.</p>
          </li>
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M8 21h8M12 17v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            </div>
            <h3>Deployment Flexibility</h3>
            <p>Deploy to Vercel, Netlify, Cloudflare, or your own server. You own the stack, we just build it.</p>
          </li>
          <li class="lp-feature-card glass">
            <div class="lp-feat-icon" aria-hidden="true">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <h3>Mobile-First Always</h3>
            <p>Every generated site is responsive by default. Pixel-perfect on mobile without any extra effort.</p>
          </li>
        </ul>
      </div>
    </section>

    <!-- ── Comparison ─────────────────────────────────────────────── -->
    <section class="lp-section lp-compare" aria-labelledby="compare-heading">
      <div class="lp-section-inner">
        <p class="lp-eyebrow">The difference</p>
        <h2 id="compare-heading">Not all website builders are equal</h2>

        <div class="lp-compare-table glass" role="table" aria-label="Website builder comparison">
          <div class="lp-compare-header" role="row">
            <div role="columnheader"></div>
            <div role="columnheader">Traditional builders</div>
            <div role="columnheader">Other AI builders</div>
            <div class="lp-compare-ours" role="columnheader">BeyondWebsite.ai</div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">No coding needed</div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div role="cell"><span class="lp-partial" aria-label="Partial">~</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">Full codebase ownership</div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">Perfect design replication</div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div role="cell"><span class="lp-partial" aria-label="Partial">~</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">SEO / AEO / GEO built-in</div>
            <div role="cell"><span class="lp-partial" aria-label="Partial">~</span></div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">Deploy anywhere</div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
          <div class="lp-compare-row" role="row">
            <div class="lp-compare-label" role="rowheader">Ready in minutes</div>
            <div role="cell"><span class="lp-no" aria-label="No">✕</span></div>
            <div role="cell"><span class="lp-partial" aria-label="Partial">~</span></div>
            <div class="lp-compare-ours" role="cell"><span class="lp-yes" aria-label="Yes">✓</span></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ── Testimonials ───────────────────────────────────────────── -->
    <section class="lp-section lp-testimonials" aria-labelledby="testimonials-heading">
      <div class="lp-section-inner">
        <p class="lp-eyebrow">What founders say</p>
        <h2 id="testimonials-heading">Trusted by teams who ship fast</h2>

        <ul class="lp-testi-grid" aria-label="Customer testimonials">
          <li class="lp-testi-card glass">
            <blockquote>
              <p>"The cost-effectiveness blew me away. We moved off WordPress in a day and the result is infinitely better — no plugins, no bloat, just a fast clean site."</p>
              <footer>
                <cite class="lp-testi-name">Ibrahim</cite>
                <span class="lp-testi-role">CEO, Rabiah Tech</span>
              </footer>
            </blockquote>
          </li>
          <li class="lp-testi-card glass">
            <blockquote>
              <p>"I gave it minimal input and it came back fully mobile-responsive. I was honestly surprised — it matched our brand better than our old designer did."</p>
              <footer>
                <cite class="lp-testi-name">Abu Abdullah</cite>
                <span class="lp-testi-role">MD, Impactify</span>
              </footer>
            </blockquote>
          </li>
          <li class="lp-testi-card glass">
            <blockquote>
              <p>"Performance went through the roof after switching. We're seeing 200+ daily clicks now. The SEO foundation it laid from day one made all the difference."</p>
              <footer>
                <cite class="lp-testi-name">Mohammed Aljimae</cite>
                <span class="lp-testi-role">Head of Operations, Logistech</span>
              </footer>
            </blockquote>
          </li>
        </ul>
      </div>
    </section>

    <!-- ── FAQ ───────────────────────────────────────────────────── -->
    <section class="lp-section lp-faq" aria-labelledby="faq-heading">
      <div class="lp-section-inner">
        <p class="lp-eyebrow">FAQ</p>
        <h2 id="faq-heading">Frequently asked questions</h2>
        <p class="lp-section-sub">Everything you need to know before you start building.</p>

        <div class="lp-faq-grid" itemscope itemtype="https://schema.org/FAQPage">
          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">Do I need coding skills to use BeyondWebsite.ai?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">No coding skills required at all. Simply paste the URL of a website you like, upload your logo and brand colours, and the AI handles everything — structure, design, content, and code — automatically.</p>
            </div>
          </details>

          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">How quickly can I get a finished website?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">Most websites are ready within minutes. The AI analyses your inspiration URL, applies your branding, and generates a fully configured, launch-ready site while you watch — no waiting days or weeks.</p>
            </div>
          </details>

          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">Do I get access to the full source code?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">Yes, absolutely. You receive the complete Nuxt 3 codebase — clean, developer-friendly code with no lock-in and no black box. Extend it, hand it to a developer, or scale it however you need.</p>
            </div>
          </details>

          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">Is SEO included out of the box?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">Yes. Every site is built with traditional SEO (meta tags, structured data, semantic HTML), AEO (AI answer engine optimisation for tools like ChatGPT and Perplexity), and GEO (generative search optimisation) built in from day one — not bolted on later.</p>
            </div>
          </details>

          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">Where can I host my website?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">Anywhere you like. Vercel, Netlify, Cloudflare Pages, a traditional VPS, or your own infrastructure. Since you own the full codebase, you have complete deployment flexibility with no platform lock-in.</p>
            </div>
          </details>

          <details class="lp-faq-item glass" itemprop="mainEntity" itemscope itemtype="https://schema.org/Question">
            <summary itemprop="name">What kinds of websites can I build?</summary>
            <div class="lp-faq-answer" itemprop="acceptedAnswer" itemscope itemtype="https://schema.org/Answer">
              <p itemprop="text">Marketing sites, SaaS landing pages, portfolio sites, company homepages, product pages, and more. If it's a web presence, BeyondWebsite.ai can create it. Each site is custom-built to match your inspiration URL, not cloned from a template.</p>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- ── CTA ────────────────────────────────────────────────────── -->
    <section class="lp-section lp-cta-section" aria-labelledby="cta-heading">
      <div class="lp-cta-card glass">
        <p class="lp-eyebrow">Get early access</p>
        <h2 id="cta-heading">Be among the first to experience effortless website creation</h2>
        <p class="lp-cta-sub">Join other founders who have already secured their spot. No credit card needed.</p>
        <RouterLink to="/login" class="lp-btn-primary lp-btn-lg">
          Start building now
          <svg width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </RouterLink>
      </div>
    </section>

    <!-- ── Footer ─────────────────────────────────────────────────── -->
    <footer class="lp-footer" role="contentinfo">
      <div class="lp-footer-inner">
        <div class="lp-footer-brand">
          <img :src="brandLogo" alt="BeyondWebsite.ai" />
        </div>
        <p class="lp-footer-copy">© {{ year }} BeyondWebsite.ai. All rights reserved.</p>
        <div class="lp-footer-links">
          <RouterLink to="/pricing" class="lp-footer-login">Pricing</RouterLink>
          <RouterLink to="/login" class="lp-footer-login">Sign in →</RouterLink>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import brandLogo from '../assets/bw-logo-wordmark.svg'

const year = computed(() => new Date().getFullYear())

// ── Scroll parallax for background orbs ──────────────────────────────
const scrollY = ref(0)
let rafPending = false

const onScroll = () => {
  if (rafPending) return
  rafPending = true
  requestAnimationFrame(() => {
    scrollY.value = window.scrollY
    rafPending = false
  })
}

onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

const orb1Style = computed(() => ({
  transform: `translate(${scrollY.value * 0.09}px, ${scrollY.value * -0.13}px)`
}))
const orb2Style = computed(() => ({
  transform: `translate(${scrollY.value * -0.07}px, ${scrollY.value * 0.10}px)`
}))
// orb3 starts centred in the viewport and drifts to bottom-left as the user scrolls
const orb3Style = computed(() => ({
  transform: `translate(${scrollY.value * -0.18}px, ${scrollY.value * 0.09}px)`
}))
</script>

<style scoped>
/* ── Root & orbs ──────────────────────────────────────────────────── */
.lp-root {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  color: var(--text-primary);
}

.lp-orb {
  position: fixed;
  border-radius: 999px;
  pointer-events: none;
  z-index: 0;
  will-change: transform;
}
.lp-orb-inner {
  width: 100%;
  height: 100%;
  border-radius: 999px;
}
.lp-orb-1 {
  width: 900px; height: 900px;
  top: -280px; left: -260px;
}
.lp-orb-1 .lp-orb-inner {
  background: radial-gradient(circle, rgba(124,109,250,0.45) 0%, rgba(124,109,250,0.15) 45%, transparent 70%);
  filter: blur(60px);
  animation: orb-breathe-1 9s ease-in-out infinite;
}
.lp-orb-2 {
  width: 780px; height: 780px;
  top: 35vh; right: -260px;
}
.lp-orb-2 .lp-orb-inner {
  background: radial-gradient(circle, rgba(56,189,248,0.35) 0%, rgba(56,189,248,0.10) 45%, transparent 70%);
  filter: blur(60px);
  animation: orb-breathe-2 12s ease-in-out infinite;
}
.lp-orb-3 {
  width: 650px; height: 650px;
  /* starts centred in the viewport; JS scroll drives it toward bottom-left */
  top: calc(50% - 325px);
  left: calc(50% - 325px);
}
.lp-orb-3 .lp-orb-inner {
  background: radial-gradient(circle, rgba(236,72,153,0.30) 0%, rgba(236,72,153,0.08) 45%, transparent 70%);
  filter: blur(60px);
  animation: orb-breathe-3 15s ease-in-out infinite;
}

@keyframes orb-breathe-1 {
  0%, 100% { transform: scale(1);    opacity: 0.90; }
  40%       { transform: scale(1.22); opacity: 1;    }
  70%       { transform: scale(0.88); opacity: 0.75; }
}
@keyframes orb-breathe-2 {
  0%, 100% { transform: scale(1);    opacity: 0.85; }
  35%       { transform: scale(1.20); opacity: 1;    }
  65%       { transform: scale(0.85); opacity: 0.70; }
}
@keyframes orb-breathe-3 {
  0%, 100% { transform: scale(1);    opacity: 0.80; }
  45%       { transform: scale(1.28); opacity: 1;    }
  80%       { transform: scale(0.82); opacity: 0.65; }
}

/* ── Nav ──────────────────────────────────────────────────────────── */
.lp-nav {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(15,15,24,0.72);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255,255,255,0.07);
}
.lp-nav-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 4vw, 3rem);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
.lp-nav-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 999px;
  padding: 4px;
}
.lp-nav-pill {
  padding: .4rem 1.1rem;
  border-radius: 999px;
  font-size: .875rem;
  font-weight: 500;
  color: rgba(255,255,255,0.65);
  text-decoration: none;
  transition: background .15s, color .15s;
}
.lp-nav-pill:hover,
.lp-nav-pill.router-link-active {
  background: rgba(255,255,255,0.1);
  color: #fff;
}
.lp-logo {
  display: flex;
  align-items: center;
  gap: .6rem;
}
.lp-logo img {
  width: 190px;
  height: auto;
  display: block;
}
.lp-logo span {
  font-weight: 800;
  font-size: .95rem;
  color: var(--text-primary);
  letter-spacing: -.02em;
}
.lp-login-btn {
  padding: .55rem 1.25rem;
  border-radius: 999px;
  background: var(--glass-bg);
  border: var(--glass-border);
  color: var(--text-secondary);
  font-size: .88rem;
  font-weight: 700;
  text-decoration: none;
  transition: all .2s;
  backdrop-filter: blur(12px);
}
.lp-login-btn:hover {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
  color: var(--text-primary);
}
.lp-nav-actions {
  display: flex;
  align-items: center;
  gap: .75rem;
}
.lp-nav-link {
  color: var(--text-secondary);
  font-size: .88rem;
  font-weight: 700;
  text-decoration: none;
  transition: color .2s;
}
.lp-nav-link:hover {
  color: var(--text-primary);
}

/* ── Common section ───────────────────────────────────────────────── */
.lp-section {
  position: relative;
  z-index: 1;
  padding: clamp(4rem, 8vw, 7rem) 0;
}
.lp-section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 4vw, 3rem);
}
.lp-eyebrow {
  margin: 0 0 .75rem;
  color: var(--accent);
  font-size: .72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .18em;
}
.lp-section > .lp-section-inner > h2,
.lp-section-inner > h2 {
  margin: 0 0 .75rem;
  font-size: clamp(1.8rem, 3.5vw, 2.8rem);
  font-weight: 820;
  letter-spacing: -.04em;
  line-height: 1.1;
  color: var(--text-primary);
}
.lp-section-sub {
  margin: 0 0 3rem;
  color: var(--text-secondary);
  font-size: clamp(.95rem, 1.3vw, 1.1rem);
  max-width: 560px;
  line-height: 1.7;
}

/* ── Hero ─────────────────────────────────────────────────────────── */
.lp-hero {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(5rem, 10vw, 9rem) clamp(1rem, 4vw, 3rem) clamp(4rem, 7vw, 6rem);
  text-align: center;
}
.lp-hero .lp-eyebrow {
  justify-content: center;
}
.lp-hero h1 {
  margin: 0 auto .5rem;
  max-width: 900px;
  font-size: clamp(2.8rem, 7vw, 6rem);
  font-weight: 870;
  line-height: .95;
  letter-spacing: -.06em;
  color: #fff;
}
.lp-gradient-text {
  background: linear-gradient(135deg, var(--accent) 0%, #38bdf8 50%, #34d399 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.lp-hero-sub {
  max-width: 620px;
  margin: 1.5rem auto 0;
  color: var(--text-secondary);
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  line-height: 1.75;
}
.lp-hero-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .75rem;
  margin-top: 2.5rem;
}
.lp-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: .5rem;
  padding: .9rem 2rem;
  background: linear-gradient(135deg, var(--accent), #34d399);
  color: #fff;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 16px 40px rgba(124,109,250,.28);
  transition: transform .2s, box-shadow .2s;
  border: none;
}
.lp-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 48px rgba(124,109,250,.38);
}
.lp-btn-lg {
  padding: 1.1rem 2.5rem;
  font-size: 1.05rem;
}
.lp-hero-note {
  color: var(--text-muted);
  font-size: .82rem;
}
.lp-hero-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: .75rem;
  max-width: 780px;
  margin: 3rem auto 0;
}
.lp-hero-stats > div {
  background: var(--glass-bg);
  border: var(--glass-border);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-radius: var(--radius-md);
  padding: 1rem 1.1rem;
  text-align: left;
}
.lp-hero-stats strong {
  display: block;
  font-size: .92rem;
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: .2rem;
}
.lp-hero-stats span {
  display: block;
  font-size: .75rem;
  color: var(--text-muted);
}

/* ── How it works ─────────────────────────────────────────────────── */
.lp-how {
  background: transparent;
}
.lp-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}
.lp-step-card {
  border-radius: var(--radius-lg);
  padding: 2rem 1.75rem;
  position: relative;
  transition: box-shadow .2s;
}
.lp-step-card:hover {
  box-shadow: var(--elev-md), 0 0 0 1px rgba(124,109,250,.15);
}
.lp-step-num {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 2.5rem;
  font-weight: 900;
  color: rgba(255,255,255,.04);
  letter-spacing: -.05em;
  line-height: 1;
  pointer-events: none;
}
.lp-step-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
  color: var(--accent);
}
.lp-step-card h3 {
  margin: 0 0 .6rem;
  font-size: 1.1rem;
  font-weight: 760;
  color: var(--text-primary);
  letter-spacing: -.02em;
}
.lp-step-card p {
  margin: 0;
  font-size: .9rem;
  color: var(--text-secondary);
  line-height: 1.7;
}

/* ── Features ─────────────────────────────────────────────────────── */
.lp-features-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
}
.lp-feature-card {
  border-radius: var(--radius-md);
  padding: 1.5rem;
  transition: box-shadow .2s;
}
.lp-feature-card:hover {
  box-shadow: var(--elev-sm), 0 0 0 1px rgba(124,109,250,.12);
}
.lp-feat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: var(--accent);
}
.lp-feature-card h3 {
  margin: 0 0 .4rem;
  font-size: 1rem;
  font-weight: 740;
  color: var(--text-primary);
  letter-spacing: -.02em;
}
.lp-feature-card p {
  margin: 0;
  font-size: .875rem;
  color: var(--text-secondary);
  line-height: 1.65;
}

/* ── Comparison table ─────────────────────────────────────────────── */
.lp-compare-table {
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-top: 2.5rem;
}
.lp-compare-header,
.lp-compare-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.2fr;
  align-items: center;
}
.lp-compare-header {
  padding: 1rem 1.5rem;
  border-bottom: var(--glass-border);
}
.lp-compare-header > div {
  font-size: .75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: var(--text-muted);
  text-align: center;
}
.lp-compare-header > div:first-child { text-align: left; }
.lp-compare-row {
  padding: .9rem 1.5rem;
  border-bottom: var(--glass-border);
  transition: background .15s;
}
.lp-compare-row:last-child { border-bottom: none; }
.lp-compare-row:hover { background: var(--glass-bg-hover); }
.lp-compare-label {
  font-size: .9rem;
  color: var(--text-secondary);
}
.lp-compare-row > div:not(.lp-compare-label) {
  text-align: center;
  font-size: 1rem;
}
.lp-compare-ours {
  background: rgba(124,109,250,.06);
}
.lp-compare-header .lp-compare-ours {
  color: var(--accent) !important;
  border-radius: 8px 8px 0 0;
}
.lp-yes   { color: var(--success); font-weight: 800; font-size: 1.05rem; }
.lp-no    { color: var(--danger);  font-weight: 800; font-size: .95rem; opacity: .8; }
.lp-partial { color: var(--warning); font-weight: 800; font-size: 1rem; }

/* ── Testimonials ─────────────────────────────────────────────────── */
.lp-testi-grid {
  list-style: none;
  margin: 2rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}
.lp-testi-card {
  border-radius: var(--radius-lg);
  padding: 1.75rem;
  transition: box-shadow .2s;
}
.lp-testi-card:hover {
  box-shadow: var(--elev-md);
}
.lp-testi-card blockquote {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.lp-testi-card blockquote p {
  margin: 0;
  font-size: .9rem;
  color: var(--text-secondary);
  line-height: 1.75;
  font-style: italic;
}
.lp-testi-card blockquote p::before { content: '"'; }
.lp-testi-card blockquote p::after  { content: '"'; }
.lp-testi-card footer {
  display: flex;
  flex-direction: column;
  gap: .2rem;
}
.lp-testi-name {
  font-size: .92rem;
  font-weight: 760;
  color: var(--text-primary);
  font-style: normal;
}
.lp-testi-role {
  font-size: .78rem;
  color: var(--text-muted);
}

/* ── CTA section ──────────────────────────────────────────────────── */
.lp-cta-section {
  padding: 0 clamp(1rem, 4vw, 3rem) clamp(4rem, 8vw, 7rem);
}
.lp-cta-card {
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 28px;
  padding: clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem);
  background: linear-gradient(135deg, rgba(124,109,250,.14) 0%, rgba(52,211,153,.07) 100%);
  position: relative;
  overflow: hidden;
  /* Centre all children */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.lp-cta-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,.07) 0%, transparent 50%);
  pointer-events: none;
}
.lp-cta-card h2 {
  max-width: 680px;
  margin: 0 0 .75rem;
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 820;
  letter-spacing: -.04em;
  line-height: 1.15;
  color: var(--text-primary);
}
.lp-cta-sub {
  color: var(--text-secondary);
  font-size: .95rem;
  margin: 0 0 2.5rem;
  max-width: 500px;
}

/* ── Footer ───────────────────────────────────────────────────────── */
.lp-footer {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255,255,255,.06);
  padding: 1.75rem clamp(1rem, 4vw, 3rem);
}
.lp-footer-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.lp-footer-brand {
  display: flex;
  align-items: center;
  gap: .5rem;
}
.lp-footer-brand img {
  width: 170px;
  height: auto;
  display: block;
}
.lp-footer-brand span {
  font-size: .85rem;
  font-weight: 760;
  color: var(--text-secondary);
}
.lp-footer-copy {
  margin: 0;
  font-size: .8rem;
  color: var(--text-muted);
}
.lp-footer-login {
  font-size: .85rem;
  font-weight: 700;
  color: var(--accent);
  text-decoration: none;
  transition: opacity .2s;
}
.lp-footer-login:hover { opacity: .75; }
.lp-footer-links {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* ── Glass helper (scoped) ────────────────────────────────────────── */
.glass {
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: var(--glass-border);
}

/* ── FAQ ──────────────────────────────────────────────────────────── */
.lp-faq-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.lp-faq-item {
  border-radius: var(--radius-md);
  overflow: hidden;
  transition: box-shadow .2s;
}
.lp-faq-item:hover {
  box-shadow: var(--elev-sm), 0 0 0 1px rgba(124,109,250,.12);
}
.lp-faq-item summary {
  padding: 1.2rem 1.4rem;
  font-size: .95rem;
  font-weight: 700;
  color: var(--text-primary);
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  user-select: none;
  transition: color .15s;
}
.lp-faq-item summary::-webkit-details-marker { display: none; }
.lp-faq-item summary::after {
  content: '+';
  font-size: 1.3rem;
  font-weight: 300;
  color: var(--accent);
  flex-shrink: 0;
  transition: transform .25s;
  line-height: 1;
}
.lp-faq-item[open] summary::after {
  transform: rotate(45deg);
}
.lp-faq-item[open] summary {
  color: var(--accent);
  border-bottom: var(--glass-border);
}
.lp-faq-answer {
  padding: 1.1rem 1.4rem;
}
.lp-faq-answer p {
  margin: 0;
  font-size: .875rem;
  color: var(--text-secondary);
  line-height: 1.75;
}

/* ── Responsive ───────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .lp-features-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 768px) {
  .lp-hero-stats   { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .lp-steps        { grid-template-columns: 1fr; }
  .lp-features-grid { grid-template-columns: 1fr; }
  .lp-testi-grid   { grid-template-columns: 1fr; }
  .lp-faq-grid     { grid-template-columns: 1fr; }

  .lp-compare-header,
  .lp-compare-row {
    grid-template-columns: 1.5fr .7fr .7fr 1fr;
    font-size: .82rem;
  }
  .lp-compare-row { padding: .75rem 1rem; }
  .lp-compare-header { padding: .75rem 1rem; }
}

@media (max-width: 480px) {
  .lp-hero-stats { grid-template-columns: 1fr 1fr; }
  .lp-compare-table { font-size: .78rem; }
  .lp-compare-header > div:nth-child(2),
  .lp-compare-row > div:nth-child(2) { display: none; }
  .lp-compare-header,
  .lp-compare-row { grid-template-columns: 1.5fr .8fr 1fr; }
  .lp-footer-inner { flex-direction: column; align-items: flex-start; gap: .5rem; }
}
</style>
